import { get } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { CamelCasedPropertiesDeep } from "type-fest";

import {
  GetConversationApiMessagesConversationsConversationIdGetData,
  GetMessageApiMessagesMessageIdGetData,
  GetMessageUsageApiMessagesMessageIdUsageGetData,
  MessageGetSchema,
  MessageUsageSchema,
} from "@:openapi";

export interface Message {
  GET_ONE: {
    RequestPath: {
      message_id: GetMessageApiMessagesMessageIdGetData["path"]["message_id"];
    };
    Response: CamelCasedPropertiesDeep<MessageGetSchema>;
  };
  GET_USAGE: {
    RequestPath: {
      message_id: GetMessageUsageApiMessagesMessageIdUsageGetData["path"]["message_id"];
    };
    Response: CamelCasedPropertiesDeep<MessageUsageSchema> | null;
  };
  GET_ALL: {
    Response: CamelCasedPropertiesDeep<Array<MessageGetSchema>>;
  };
  GET_ALL_MESSAGE_ON_CHAT: {
    RequestPath: {
      conversationId: GetConversationApiMessagesConversationsConversationIdGetData["path"]["conversation_id"];
    };
    Response: CamelCasedPropertiesDeep<Array<MessageGetSchema>>;
  };
}

export const messageQueryKey = createQueryKeys("message", {
  getOne: (params: Message["GET_ONE"]["RequestPath"]) => [params],
  getUsage: (params: Message["GET_USAGE"]["RequestPath"]) => [params],
  getAll: null,
  getAllMessagesByConversationId: (
    params: Message["GET_ALL_MESSAGE_ON_CHAT"]["RequestPath"],
  ) => [params],
});

export const useQueryGetMessageUsage = (
  params: Message["GET_USAGE"]["RequestPath"],
  options?: Omit<
    UseQueryOptions<Message["GET_USAGE"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Message["GET_USAGE"]["Response"], AxiosError>({
    queryKey: messageQueryKey.getUsage(params).queryKey,
    queryFn: () => {
      return get<Message["GET_USAGE"]["Response"]>(
        `/api/messages/${params.message_id}/usage`,
      );
    },
    ...options,
  });
};

export const useQueryGetOneMessage = (
  params: Message["GET_ONE"]["RequestPath"],
  options?: Omit<
    UseQueryOptions<Message["GET_ONE"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Message["GET_ONE"]["Response"], AxiosError>({
    queryKey: messageQueryKey.getOne(params).queryKey,
    queryFn: () => {
      return get<Message["GET_ONE"]["Response"]>(
        `/api/messages/${params.message_id}`,
      );
    },
    ...options,
  });
};

export const useQueryGetAllMessagesByConversationId = (
  params: Message["GET_ALL_MESSAGE_ON_CHAT"]["RequestPath"],
  options?: Omit<
    UseQueryOptions<Message["GET_ALL_MESSAGE_ON_CHAT"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Message["GET_ALL_MESSAGE_ON_CHAT"]["Response"], AxiosError>({
    queryKey: messageQueryKey.getAllMessagesByConversationId(params).queryKey,
    queryFn: () => {
      if (params.conversationId === "new") {
        return Promise.resolve([]);
      }
      return get<Message["GET_ALL_MESSAGE_ON_CHAT"]["Response"]>(
        `/api/messages/conversations/${params.conversationId}`,
      );
    },
    ...options,
  });
};

export const useQueryGetAllMessages = (
  options?: Omit<
    UseQueryOptions<Message["GET_ALL"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Message["GET_ALL"]["Response"], AxiosError>({
    queryKey: messageQueryKey.getAll.queryKey,
    queryFn: () => {
      return get<Message["GET_ALL"]["Response"]>("/api/messages/");
    },
    ...options,
  });
};
